import { useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

import { MINIMUM_DISTRIBUTION_COUNT } from '../../../constants';
import {
  alignBottom,
  alignHorizontalCenter,
  alignLeft,
  alignRight,
  alignTop,
  alignVerticalCenter,
  distributeHorizontalSpacing,
  distributeVerticalSpacing,
  type SelectionBounds,
} from '../../../utils/layout/nodeAlignment';
import { useAlignmentShortcuts } from '../../shortcuts/useAlignmentShortcuts';
import { useLayoutCommandExecutor } from './useLayoutCommandExecutor';

function getViewportBounds(
  screenToFlowPosition: (pos: { x: number; y: number }) => { x: number; y: number },
): SelectionBounds | undefined {
  const container = document.querySelector('.react-flow');

  if (!container) {
    return undefined;
  }

  const rect = container.getBoundingClientRect();
  const topLeft = screenToFlowPosition({ x: rect.left, y: rect.top });
  const bottomRight = screenToFlowPosition({ x: rect.right, y: rect.bottom });

  return {
    height: bottomRight.y - topLeft.y,
    maxX: bottomRight.x,
    maxY: bottomRight.y,
    minX: topLeft.x,
    minY: topLeft.y,
    width: bottomRight.x - topLeft.x,
  };
}

export function useAlignNodes() {
  const { screenToFlowPosition } = useReactFlow();
  const executeLayout = useLayoutCommandExecutor();

  const onAlignLeft = useCallback(
    (toViewport: boolean) =>
      executeLayout((rects) =>
        alignLeft({ bounds: toViewport ? getViewportBounds(screenToFlowPosition) : undefined, nodes: rects }),
      ),
    [executeLayout, screenToFlowPosition],
  );
  const onAlignRight = useCallback(
    (toViewport: boolean) =>
      executeLayout((rects) =>
        alignRight({ bounds: toViewport ? getViewportBounds(screenToFlowPosition) : undefined, nodes: rects }),
      ),
    [executeLayout, screenToFlowPosition],
  );
  const onAlignTop = useCallback(
    (toViewport: boolean) =>
      executeLayout((rects) =>
        alignTop({ bounds: toViewport ? getViewportBounds(screenToFlowPosition) : undefined, nodes: rects }),
      ),
    [executeLayout, screenToFlowPosition],
  );
  const onAlignBottom = useCallback(
    (toViewport: boolean) =>
      executeLayout((rects) =>
        alignBottom({ bounds: toViewport ? getViewportBounds(screenToFlowPosition) : undefined, nodes: rects }),
      ),
    [executeLayout, screenToFlowPosition],
  );
  const onAlignHorizontalCenter = useCallback(
    (toViewport: boolean) =>
      executeLayout((rects) =>
        alignHorizontalCenter({
          bounds: toViewport ? getViewportBounds(screenToFlowPosition) : undefined,
          nodes: rects,
        }),
      ),
    [executeLayout, screenToFlowPosition],
  );
  const onAlignVerticalCenter = useCallback(
    (toViewport: boolean) =>
      executeLayout((rects) =>
        alignVerticalCenter({ bounds: toViewport ? getViewportBounds(screenToFlowPosition) : undefined, nodes: rects }),
      ),
    [executeLayout, screenToFlowPosition],
  );

  const onDistributeHorizontal = useCallback(
    () => executeLayout((rects) => distributeHorizontalSpacing({ nodes: rects }), MINIMUM_DISTRIBUTION_COUNT),
    [executeLayout],
  );
  const onDistributeVertical = useCallback(
    () => executeLayout((rects) => distributeVerticalSpacing({ nodes: rects }), MINIMUM_DISTRIBUTION_COUNT),
    [executeLayout],
  );

  useAlignmentShortcuts({
    onAlignBottom,
    onAlignHorizontalCenter,
    onAlignLeft,
    onAlignRight,
    onAlignTop,
    onAlignVerticalCenter,
    onDistributeHorizontal,
    onDistributeVertical,
  });

  return {
    onAlignBottom,
    onAlignHorizontalCenter,
    onAlignLeft,
    onAlignRight,
    onAlignTop,
    onAlignVerticalCenter,
    onDistributeHorizontal,
    onDistributeVertical,
  };
}
